// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import FlipMove from 'react-flip-move';

import deepEqual from 'fast-deep-equal';
import omit from 'lodash/omit';

import type { AuthZInfo } from '@ta/common.authz';
import { featureIsEnabled } from '@ta/common.features';
import { HasLocationPermission } from '@ta/common.geolocation';
import { getObjectId } from '@ta/common.global';
import { localize } from '@ta/common.i18n';
import { InfiniteLoader } from '@ta/common.infinite-loader';
import localStorage from '@ta/common.localstorage';
import Logging from '@ta/common.logging';
import { shallowEquals } from '@ta/common.object-util';
import { RouteConsumer, parseUrl, buildUrl, type NavigateFunc } from '@ta/common.routing';
import type { Route } from '@ta/common.routing';
import WindowResize from '@ta/events.window-resize';
import WindowScroll from '@ta/events.window-scroll';
import { VideoPlayerProvider } from '@ta/media.common';
import {
  ItemIdContext,
  InteractionLogger,
  FeedSectionIdContext,
  TrackingNamespaceContext,
} from '@ta/tracking.interactions';
import { UntagAction } from '@ta/social.actions';
import { sessionType as getSessionType } from '@ta/social.feed';
import { DeleteAction, getState, type ContentTypePairKey } from '@ta/social.content-removal';
import { ForumsListLayout, HeaderSection, LocationPermissionSection } from '@ta/social.sections';
import { ShowMore } from '@ta/social.show-more';
import { FeedLoadingSkeleton } from '@ta/social.skeletons';
import { FeedImpressionLogger, type TrackFeedImpression } from '@ta/tracking.impressions';
import AdTarget from '@ta/cpm.ad-target';

import DMO_COMPETITOR_GEOS from './competitorGeosList';
import FeedSection from './FeedSection';
import renderItem from './sectionRenderer';

/** ActivityAPIQuery returns GraphQL props for:
   sections, items, & object
   __typename refers to object type requested - can be one of the following:
   Review, Photo, Video, ForumPost, LinkPost, Trip
*/

const INFINITE_LOADER_THRESHOLD = 3000;
const INFINITE_LOADER_THROTTLE = 50;
const LAST_VIS_SECTION_THROTTLE = 1000;
const PAGE_SIZE = 20;

const adPositions: Map<number, string> = new Map([
  // got nothing to say, just want this to be multiline
  [3, 'inline1'],
  [14, 'inline2'],
]);

// Minimum pixels visible to consider the section visible
const MIN_VISIBLE_SECTION_THRESHOLD = 60;

const mixerNamespace = 'mixer';

type ConnectedProps = {|
  loggedInUserId: string,
  suppressedContent: ContentTypePairKey[],
  sessionType: string,
|};

type ExternalProps = {|
  userId: string,
  member: any,
  socialFeed: any,
  refetch: any => Promise<*>,
  updateFeed: (boolean, ?(void) => void) => Promise<*>,
  error: any,
  fetchMore: any,
  mixerPlacement: string,
  allowedTypes?: string[],
  locationId: number,
  viewportCategory: string,
  puid: ?string,
  feedIdToRestore?: string,
  reset?: boolean,
  sessionId?: string,
  card?: any,
  newContentSubmittedTimestamp?: Date,
  loggedInMemberAuthorization?: AuthZInfo,
  isActive: boolean,
|};
type ActivityObjectsContainerProps = {
  ...ExternalProps,
  ...ConnectedProps,
};

type OnMountProps = { callback: () => any, children: any };

/**
 * Used by other applications to know how far down the user has scrolled
 * This is not perfect as it is only for this one browser
 */
const updateMaxScrolledSection = (sectionId: string) => {
  const MAX_TA_SOCIAL_SCROLL_KEY = 'maxTASocialScroll';
  const sectionParts = sectionId.split('_');
  if (sectionParts && sectionParts.length > 1) {
    const sectionIndex = parseInt(sectionParts[sectionParts.length - 1], 10);
    const currentMaxSectionIndex = localStorage.get(MAX_TA_SOCIAL_SCROLL_KEY);
    if (sectionIndex && (!currentMaxSectionIndex || sectionIndex > currentMaxSectionIndex)) {
      localStorage.set(MAX_TA_SOCIAL_SCROLL_KEY, sectionIndex);
    }
  }
};

class ExecuteOnMount extends React.Component<OnMountProps> {
  componentDidMount() {
    const { callback } = this.props;
    if (callback) {
      callback();
    }
  }

  render() {
    return this.props.children;
  }
}

/**
 * Container for the actual list of sections in the activity feed.
 */
// eslint-disable-next-line react/no-multi-comp
class ActivityObjectsContainer extends React.Component<ActivityObjectsContainerProps> {
  componentDidMount() {
    // eslint-disable-next-line no-restricted-globals
    window.addEventListener('popstate', this.checkPopState);
  }

  componentDidUpdate() {
    // Restore fid if it needs to be there:
    if (this.lastVisibleSectionId) {
      this.fixupHistoryWithImpressionId(undefined);
    }
  }

  componentWillUnmount() {
    // eslint-disable-next-line no-restricted-globals
    window.removeEventListener('popstate', this.checkPopState);
  }

  /**
   * Recorded by fixupHistoryWithImpressionId as it's munging the current state
   * Used by checkPopState in order to prevent browser from reloading the page.
   */
  replacedUrlToRestoreOnPop;

  /**
   * References to all sections in the feed, a dictionary mapping section Ids to component refs.
   * These are used to:
   * 1) detmerine the bottom-most visible section and send it to the mixer for tracking
   * 2) scroll to the last-bottom-most visible section when that comes back from the mixer on feed restoration.
   */
  allSectionRefs = {};

  /**
   * Last "meaningfully visible" section sent to the server.  Used for optimizing the network calls so that
   * we don't keep sending the same one on small scroll events.
   */
  lastVisibleSectionId;

  // eslint-disable-next-line class-methods-use-this
  constructRouteFromCurrentLocation() {
    // eslint-disable-next-line no-restricted-globals
    const pathname = window.location.pathname === '/' ? '' : window.location.pathname;
    // eslint-disable-next-line no-restricted-globals
    const parsed: ?Route = parseUrl(`${pathname}/${window.location.search}`);
    const currentRoute: Route = parsed || { page: 'Home' };
    return currentRoute;
  }

  /**
   * This is necessary to prevent the browser from requesting a full page load on Back event.
   * The trick is to restore the URL to what the Browser internally knows was the originally loaded one
   */
  checkPopState = (event: any) => {
    if (event.state && event.state.fid && this.replacedUrlToRestoreOnPop) {
      // eslint-disable-next-line no-restricted-globals
      window.history.replaceState(null, '', this.replacedUrlToRestoreOnPop);
    }
  };

  /**
   * Used to fix up the current browser address to include fid tracker.
   * In theory, navigate with relpaceHistory: true could work here
   *   something like: navigate(newRoute);
   * but we actually need to replace State, and navigate does not yet support it.
   */
  // eslint-disable-next-line no-unused-vars
  fixupHistoryWithImpressionId(navigate?: NavigateFunc) {
    if (
      featureIsEnabled('social_allow_feed_restore') &&
      this.props.socialFeed &&
      this.props.isActive
    ) {
      const newRoute = this.constructRouteFromCurrentLocation();
      if (newRoute.fid && newRoute.fid === this.props.socialFeed.impressionId) {
        // Already set
        return;
      }

      newRoute.fid = this.props.socialFeed.impressionId;
      const newURL = buildUrl(newRoute);

      // Make sure we are not accidentally switching pathnames!
      // eslint-disable-next-line no-restricted-globals
      if (newURL && window.location.pathname && newURL.indexOf(window.location.pathname) === -1) {
        return;
      }

      // eslint-disable-next-line no-restricted-globals
      window.history.replaceState(newRoute, '', newURL);

      // This is necessary in order to restore the URL at the last moment during pop, to prevent browser reload
      if (!this.replacedUrlToRestoreOnPop) {
        // eslint-disable-next-line no-restricted-globals
        this.replacedUrlToRestoreOnPop = window.location.href;
      }
    }
  }

  /**
   * This is not going to make excessive server calls, because:
   * - reactions to scrolls/resizes are throttled - see the exact parameters in the invocation below
   * - we only report changes in the bottom-most visible card if it's different from the last one sent to the server.
   */
  checkForLastVisibleSectionChange(
    trackFeedImpression: TrackFeedImpression,
    navigate: ?NavigateFunc,
  ) {
    // eslint-disable-next-line no-restricted-globals
    const curWindowHeight = window.innerHeight;
    let lastVisSec: React.ElementRef<*>;
    let lastVisSecId;
    Object.entries(this.allSectionRefs).forEach(([secId, secRefMixed]) => {
      if (secRefMixed) {
        const secRef: React.ElementRef<*> = secRefMixed;

        if (secRef.current) {
          // We want the top to be above the bottom by at least the threshold.
          if (
            secRef.current.getBoundingClientRect().top + MIN_VISIBLE_SECTION_THRESHOLD <=
            curWindowHeight
          ) {
            // And now we are looking for the bottom-most candidate
            if (
              !lastVisSec ||
              lastVisSec.current.getBoundingClientRect().top <
                secRef.current.getBoundingClientRect().top
            ) {
              lastVisSec = secRef;
              lastVisSecId = secId;
            }
          }
        }
      }
    });

    if (lastVisSecId && lastVisSecId !== this.lastVisibleSectionId) {
      if (navigate) {
        // Only set this when actually changing state/url.
        // The other case is on mount, only for server notification.
        this.lastVisibleSectionId = lastVisSecId;

        this.fixupHistoryWithImpressionId(navigate);
      }

      updateMaxScrolledSection(lastVisSecId);

      trackFeedImpression(
        ({
          allowedTypes: this.props.allowedTypes || [],
          locationId: this.props.locationId || 0,
          owner: this.props.userId,
          placement: this.props.mixerPlacement,
          puid: this.props.puid,
          sessionType: this.props.sessionType,
          sectionId: lastVisSecId,
        }: any),
      );
    }
  }

  render() {
    const isCompetitorDMOGeo = DMO_COMPETITOR_GEOS.has(Number(this.props.locationId));
    // Discover the best of .*
    const discoverBestOf = new RegExp(
      localize('mercury_cluster_header_discover_best_of_geo', {
        sGeo: '.*',
      }),
    );
    let discoverBestOfIndex;
    const getItems = (section, sectionIndex, deleteObject, untagObject) => {
      // ADS-6666 tracking event
      const dmoCompetitorTracker = isCompetitorDMOGeo && sectionIndex - discoverBestOfIndex === 3;
      const { actor, items } = section;
      if (section.type === 'HEADER') {
        if (discoverBestOf.test(section.title)) {
          discoverBestOfIndex = sectionIndex;
        }
        const sectionRef: React.ElementRef<*> =
          this.allSectionRefs[section.id] || React.createRef();
        this.allSectionRefs[section.id] = sectionRef;
        return (
          <FeedSection section={section} key={section.id}>
            {() => (
              <div ref={sectionRef}>
                <HeaderSection title={section.title} uxHints={section.uxHints} />
              </div>
            )}
          </FeedSection>
        );
      }

      const firstItem = (items && items.length) > 0 ? items[0] : null;
      if (
        section.type === 'PROMPT' &&
        firstItem &&
        firstItem.object.__typename === 'EnableLocationPrompt'
      ) {
        const locationPermissionSection = () => (
          <FeedSection section={section} key={section.id}>
            {() => <LocationPermissionSection item={firstItem.object} />}
          </FeedSection>
        );

        return (
          <HasLocationPermission
            hasPermission={() => null}
            deniedPermission={() => null}
            promptPermission={locationPermissionSection} /** user hasn't given us permission yet */
            positioningOff={locationPermissionSection} /** location service isn't enabled */
          />
        );
      }

      const resultItems = items
        .filter((item, index) => index === 0) // Use the first items as a rep. for their sections
        .filter(
          // if we are forcing a card to the top of the feed, we don't want to show it elsewhere in the feed.
          item =>
            !this.props.card ||
            `${item.object.__typename}_${getObjectId(item.object)}` !== this.props.card,
        )
        .filter(item => item !== null)
        .filter(item => {
          const objectId = getObjectId(item.object);
          const contentType = item.object.__typename;
          const { suppressedContent } = this.props;
          return !suppressedContent.some(content => deepEqual(content, [contentType, objectId]));
        })
        .map(item => {
          // make refs to each section for scroll tracking
          const sectionRef: React.ElementRef<*> =
            this.allSectionRefs[section.id] || React.createRef();
          this.allSectionRefs[section.id] = sectionRef;
          const itemId =
            item.id || item.object.id || item.object.postId || item.object.repostId || sectionIndex;

          return (
            <FeedSection section={section} key={itemId}>
              {() => (
                <div ref={sectionRef}>
                  <FeedSectionIdContext.Provider value={section.id}>
                    {/* TODO: This creates a new context every render. Change object creation to not be inline or make it so the value is just a number */}
                    <ItemIdContext.Provider value={{ itemId }}>
                      {renderItem({
                        mixerPlacement: this.props.mixerPlacement,
                        puid: this.props.puid,
                        locationId: this.props.locationId,
                        viewportCategory: this.props.viewportCategory,
                        member: this.props.member,
                        section,
                        actor,
                        propsItem: item,
                        sectionId: section.id,
                        deleteObjectFunc: deleteObject,
                        loggedInMemberAuthorization: this.props.loggedInMemberAuthorization,
                        untagObjectFunc: untagObject,
                        loggedInUserId: this.props.loggedInUserId,
                        dmoCompetitorTracker,
                      })}
                    </ItemIdContext.Provider>
                  </FeedSectionIdContext.Provider>
                </div>
              )}
            </FeedSection>
          );
        });
      return resultItems;
    };

    const allowedSections = [
      'VIDEO',
      'VIDEOS',
      'MEDIA_BATCH',
      'PHOTO',
      'TRIP',
      'TRIPS',
      'LINK_POST',
      'REPOST',
    ];
    // adjust the ad index so we skip recommended and featured content
    const adIndexOffset = this.props.socialFeed.sections
      .slice(0, PAGE_SIZE)
      .findIndex(section => allowedSections.includes(section.type));

    const injectAd = (
      accumulator: React$Element<any> | React$Element<any>[],
      elem: React$Element<any>,
      index: number,
    ) => {
      const list = [].concat(accumulator).concat(elem);
      const adjustedIndex = index - adIndexOffset;
      if (
        featureIsEnabled('mw_social_feed_inline_ads') &&
        !this.props.loggedInMemberAuthorization &&
        adPositions.has(adjustedIndex)
      ) {
        list.push(
          <AdTarget
            size="300x250"
            position={adPositions.get(adjustedIndex)}
            pageLoadRender={index < PAGE_SIZE}
            centered
            withMargin
          />,
        );
      }
      return list;
    };

    const sectionList = [
      // VideoPlayerProvider ensures that all VideoPlayers are aware of each other, hence we autoplay only one of them if multiple are visible at the same time
      // attach deferred rendering as skeleton views after slicing the server rendered stuff
      <VideoPlayerProvider key={0.1}>
        <DeleteAction>
          {deleteObject => (
            <UntagAction>
              {untagObject => (
                <FlipMove
                  staggerDurationBy="30"
                  duration={400}
                  enterAnimation="none"
                  leaveAnimation="none"
                >
                  {this.props.socialFeed.sections
                    .map((section, sectionIndex) =>
                      getItems(section, sectionIndex, deleteObject, untagObject),
                    )
                    .reduce(injectAd)}
                </FlipMove>
              )}
            </UntagAction>
          )}
        </DeleteAction>
      </VideoPlayerProvider>,
    ];

    return (
      <RouteConsumer>
        {({ route, navigate }) => (
          <React.Fragment>
            <FeedImpressionLogger>
              {trackFeedImpression => {
                const scrollCallback = () =>
                  this.checkForLastVisibleSectionChange(trackFeedImpression, navigate);
                const mountCallback = () =>
                  this.checkForLastVisibleSectionChange(trackFeedImpression, null);

                return (
                  <React.Fragment>
                    <WindowScroll callback={scrollCallback} throttle={LAST_VIS_SECTION_THROTTLE} />
                    <WindowResize callback={scrollCallback} throttle={LAST_VIS_SECTION_THROTTLE} />
                    <ExecuteOnMount callback={mountCallback}>
                      {route.tab === 'forum' ? (
                        <ForumsListLayout>{sectionList}</ForumsListLayout>
                      ) : (
                        <React.Fragment>{sectionList}</React.Fragment>
                      )}
                    </ExecuteOnMount>
                  </React.Fragment>
                );
              }}
            </FeedImpressionLogger>
          </React.Fragment>
        )}
      </RouteConsumer>
    );
  }
}

type FeedState = {
  showingTopSkeletons: boolean,
  showingBottomSkeletons: boolean,
};

// eslint-disable-next-line react/no-multi-comp
class Feed extends React.Component<ActivityObjectsContainerProps, FeedState> {
  state = {
    showingTopSkeletons: false,
    showingBottomSkeletons: this.shouldStartWithfiniteScroll(),
  };

  shouldComponentUpdate(nextProps: ActivityObjectsContainerProps, nextState: FeedState) {
    // The refetch, fetchMore and updateFeed methods are passed as render props from react-apollo so we get a new instance
    // each time, which can cause unnecessary re-renders.
    const safeToIgnoreUpdates = [
      'refetch',
      'fetchMore',
      'updateFeed',
      'feedIdToRestore',
      'fid',
      'member',
    ];

    // some values need full checks to allow safe reconstruction of said values
    const requiresDeepEqualCheck = ['allowedTypes'];

    const ignorableDuringShallowEquals = safeToIgnoreUpdates.concat(requiresDeepEqualCheck);
    const propsDiffers = !shallowEquals(
      omit(nextProps, ignorableDuringShallowEquals),
      omit(this.props, ignorableDuringShallowEquals),
    );

    // Deep check as needed
    let deepChecksDiffer = false;
    for (let idx = 0; idx < requiresDeepEqualCheck.length; idx++) {
      deepChecksDiffer =
        deepChecksDiffer ||
        !deepEqual(this.props[requiresDeepEqualCheck[idx]], nextProps[requiresDeepEqualCheck[idx]]);
    }

    const stateDiffers = !shallowEquals(this.state, nextState);
    return propsDiffers || stateDiffers || deepChecksDiffer;
  }

  componentDidUpdate(prevProps: Object) {
    // when a new photo/video is uploaded we want to refetch
    const prevTstamp = prevProps.newContentSubmittedTimestamp
      ? prevProps.newContentSubmittedTimestamp.getTime()
      : 0;
    const currTstamp = this.props.newContentSubmittedTimestamp
      ? this.props.newContentSubmittedTimestamp.getTime()
      : 0;

    if (currTstamp > prevTstamp) {
      // Why force reset?
      // In order to ensure we get the new content on top (as promised by mixer)
      // Sometimes, reset will be true in the original query, but it is not guaranteed (e.g. in the case
      // of a restored feed, it can be false).
      this.props.refetch({ reset: true });
    }
  }

  // If we restored a feed, and it wasn't the first page, we are starting with infinite scroller turned on:
  shouldStartWithfiniteScroll() {
    return (
      !!this.props.feedIdToRestore && this.props.socialFeed && this.props.socialFeed.hasPrevious
    );
  }

  render() {
    return this.props.socialFeed === undefined ? null : (
      <TrackingNamespaceContext.Provider value={mixerNamespace}>
        <InfiniteLoader
          hasMore={this.props.socialFeed.hasMore}
          loadMore={() => this.props.updateFeed(false)}
          throttle={INFINITE_LOADER_THROTTLE}
          threshold={INFINITE_LOADER_THRESHOLD}
          infiniteLoadOnMount={this.shouldStartWithfiniteScroll()}
        >
          {(containerRef, contentRef, startInfiniteLoad) => (
            <div id="content" ref={contentRef}>
              <InteractionLogger>
                {logShowMoreClick => (
                  <ShowMore
                    onClick={e => {
                      this.setState({ showingTopSkeletons: true });
                      logShowMoreClick(e, 'showMore', {
                        mixerImpressionId: this.props.socialFeed
                          ? this.props.socialFeed.impressionId
                          : '',
                      });
                      this.props.updateFeed(true, () =>
                        this.setState({ showingTopSkeletons: false }),
                      );
                    }}
                    hasMore={this.props.socialFeed ? this.props.socialFeed.hasPrevious : false}
                    showButton={!this.state.showingTopSkeletons}
                    goingBackwards
                  >
                    <FeedLoadingSkeleton numSections={1} />
                  </ShowMore>
                )}
              </InteractionLogger>
              <RouteConsumer>
                {({ route: { card } }) => (
                  <ActivityObjectsContainer
                    {...this.props}
                    card={card}
                    error={this.props.error}
                    member={this.props.member}
                  />
                )}
              </RouteConsumer>
              <InteractionLogger>
                {logShowMoreClick => (
                  <ShowMore
                    onClick={e => {
                      if (!this.state.showingBottomSkeletons) {
                        this.setState({ showingBottomSkeletons: true });
                        // This will trigger update as the skeletons will become visible!
                      } else {
                        Logging.error(
                          'Skeletons were visible, yet the button got clicked?!  We may be missing a feed update.',
                        );
                      }

                      logShowMoreClick(e, 'showMore', {
                        mixerImpressionId: this.props.socialFeed.impressionId,
                      });
                      startInfiniteLoad();
                    }}
                    hasMore={this.props.socialFeed.hasMore}
                    showButton={!this.state.showingBottomSkeletons}
                  >
                    <FeedLoadingSkeleton numSections={1} />
                  </ShowMore>
                )}
              </InteractionLogger>
            </div>
          )}
        </InfiniteLoader>
      </TrackingNamespaceContext.Provider>
    );
  }
}

export default connect<ActivityObjectsContainerProps, ExternalProps, _, _, _, _>(state => ({
  loggedInUserId: state.auth.loggedInUserId,
  suppressedContent: getState(state).suppressedContent,
  sessionType: getSessionType(state.meta.device.viewportCategory),
}))(Feed);
